/**
 * Performance monitoring utilities for tracking web vitals and performance metrics
 *
 * Tracks:
 * - First Contentful Paint (FCP)
 * - Time to Interactive (TTI)
 * - Cumulative Layout Shift (CLS)
 * - Scroll performance (60fps target)
 */

interface PerformanceMetrics {
  fcp?: number
  lcp?: number
  fid?: number
  cls?: number
  ttfb?: number
  tti?: number
}

interface ScrollPerformanceMetrics {
  averageFps: number
  minFps: number
  maxFps: number
  droppedFrames: number
  totalFrames: number
}

type PerformanceCallback = (metrics: PerformanceMetrics) => void

let performanceObserver: PerformanceObserver | null = null
let metricsCallback: PerformanceCallback | null = null
const metrics: PerformanceMetrics = {}

/**
 * Initialize performance monitoring
 */
export function initPerformanceMonitoring(callback?: PerformanceCallback): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    console.warn('Performance monitoring not supported in this browser')
    return
  }

  metricsCallback = callback || null

  // Observe paint timing (FCP, LCP)
  try {
    const paintObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          metrics.fcp = entry.startTime
          reportMetric('FCP', entry.startTime)
        }
      }
    })
    paintObserver.observe({ entryTypes: ['paint'] })
  } catch (e) {
    console.warn('Paint timing not supported', e)
  }

  // Observe Largest Contentful Paint (LCP)
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const lastEntry = entries[entries.length - 1]
      metrics.lcp = lastEntry.startTime
      reportMetric('LCP', lastEntry.startTime)
    })
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
  } catch (e) {
    console.warn('LCP not supported', e)
  }

  // Observe First Input Delay (FID)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const fidEntry = entry as any
        metrics.fid = fidEntry.processingStart - fidEntry.startTime
        reportMetric('FID', metrics.fid)
      }
    })
    fidObserver.observe({ entryTypes: ['first-input'] })
  } catch (e) {
    console.warn('FID not supported', e)
  }

  // Observe Cumulative Layout Shift (CLS)
  try {
    let clsValue = 0
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const layoutShiftEntry = entry as any
        if (!layoutShiftEntry.hadRecentInput) {
          clsValue += layoutShiftEntry.value
          metrics.cls = clsValue
          reportMetric('CLS', clsValue)
        }
      }
    })
    clsObserver.observe({ entryTypes: ['layout-shift'] })
  } catch (e) {
    console.warn('CLS not supported', e)
  }

  // Measure Time to First Byte (TTFB)
  try {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    if (navigationEntry) {
      metrics.ttfb = navigationEntry.responseStart - navigationEntry.requestStart
      reportMetric('TTFB', metrics.ttfb)
    }
  } catch (e) {
    console.warn('TTFB measurement failed', e)
  }

  // Estimate Time to Interactive (TTI)
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const tti = performance.now()
        metrics.tti = tti
        reportMetric('TTI', tti)
      }, 0)
    })
  }
}

/**
 * Report a performance metric
 */
function reportMetric(name: string, value: number): void {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Performance] ${name}: ${value.toFixed(2)}ms`)
  }

  // Call callback if provided
  if (metricsCallback) {
    metricsCallback(metrics)
  }

  // Send to analytics service (placeholder)
  // In production, you would send this to your analytics service
  // e.g., Google Analytics, Datadog, New Relic, etc.
}

/**
 * Get current performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  return { ...metrics }
}

/**
 * Monitor scroll performance
 * Returns metrics about frame rate during scrolling
 */
export function monitorScrollPerformance(
  element: HTMLElement | Window = window,
  duration: number = 5000
): Promise<ScrollPerformanceMetrics> {
  return new Promise((resolve) => {
    const frameTimestamps: number[] = []
    let animationFrameId: number
    let startTime: number
    let isScrolling = false

    const measureFrame = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp
      }

      if (isScrolling) {
        frameTimestamps.push(timestamp)
      }

      const elapsed = timestamp - startTime
      if (elapsed < duration) {
        animationFrameId = requestAnimationFrame(measureFrame)
      } else {
        cleanup()
        resolve(calculateScrollMetrics(frameTimestamps))
      }
    }

    const handleScroll = () => {
      isScrolling = true
    }

    const handleScrollEnd = () => {
      isScrolling = false
    }

    const cleanup = () => {
      element.removeEventListener('scroll', handleScroll)
      element.removeEventListener('scrollend', handleScrollEnd)
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }

    element.addEventListener('scroll', handleScroll, { passive: true })
    element.addEventListener('scrollend', handleScrollEnd, { passive: true })

    animationFrameId = requestAnimationFrame(measureFrame)
  })
}

/**
 * Calculate scroll performance metrics from frame timestamps
 */
function calculateScrollMetrics(timestamps: number[]): ScrollPerformanceMetrics {
  if (timestamps.length < 2) {
    return {
      averageFps: 0,
      minFps: 0,
      maxFps: 0,
      droppedFrames: 0,
      totalFrames: timestamps.length
    }
  }

  const frameDurations: number[] = []
  for (let i = 1; i < timestamps.length; i++) {
    frameDurations.push(timestamps[i] - timestamps[i - 1])
  }

  const fps = frameDurations.map(duration => 1000 / duration)
  const averageFps = fps.reduce((sum, f) => sum + f, 0) / fps.length
  const minFps = Math.min(...fps)
  const maxFps = Math.max(...fps)

  // Count frames below 60fps as dropped
  const droppedFrames = fps.filter(f => f < 60).length

  return {
    averageFps: Math.round(averageFps),
    minFps: Math.round(minFps),
    maxFps: Math.round(maxFps),
    droppedFrames,
    totalFrames: timestamps.length
  }
}

/**
 * Log scroll performance metrics
 */
export async function logScrollPerformance(
  element?: HTMLElement | Window,
  duration?: number
): Promise<void> {
  const metrics = await monitorScrollPerformance(element, duration)

  console.log('[Scroll Performance]', {
    averageFps: `${metrics.averageFps} fps`,
    minFps: `${metrics.minFps} fps`,
    maxFps: `${metrics.maxFps} fps`,
    droppedFrames: `${metrics.droppedFrames} / ${metrics.totalFrames}`,
    performance: metrics.averageFps >= 60 ? '✓ Good' : metrics.averageFps >= 30 ? '⚠ Fair' : '✗ Poor'
  })
}

/**
 * Measure component render time
 */
export function measureRenderTime(componentName: string): () => void {
  const startTime = performance.now()

  return () => {
    const endTime = performance.now()
    const duration = endTime - startTime

    if (import.meta.env.DEV) {
      console.log(`[Render Time] ${componentName}: ${duration.toFixed(2)}ms`)
    }
  }
}

/**
 * Mark a custom performance measurement
 */
export function markPerformance(name: string): void {
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(name)
  }
}

/**
 * Measure time between two performance marks
 */
export function measurePerformance(name: string, startMark: string, endMark: string): number | null {
  if (typeof performance !== 'undefined' && performance.measure) {
    try {
      performance.measure(name, startMark, endMark)
      const measure = performance.getEntriesByName(name)[0]
      return measure ? measure.duration : null
    } catch (e) {
      console.warn('Performance measurement failed', e)
      return null
    }
  }
  return null
}

/**
 * Get Web Vitals summary
 */
export function getWebVitalsSummary(): {
  metric: string
  value: number | undefined
  rating: 'good' | 'needs-improvement' | 'poor' | 'unknown'
}[] {
  const getClsRating = (cls?: number) => {
    if (cls === undefined) return 'unknown'
    if (cls <= 0.1) return 'good'
    if (cls <= 0.25) return 'needs-improvement'
    return 'poor'
  }

  const getFcpRating = (fcp?: number) => {
    if (fcp === undefined) return 'unknown'
    if (fcp <= 1800) return 'good'
    if (fcp <= 3000) return 'needs-improvement'
    return 'poor'
  }

  const getLcpRating = (lcp?: number) => {
    if (lcp === undefined) return 'unknown'
    if (lcp <= 2500) return 'good'
    if (lcp <= 4000) return 'needs-improvement'
    return 'poor'
  }

  const getFidRating = (fid?: number) => {
    if (fid === undefined) return 'unknown'
    if (fid <= 100) return 'good'
    if (fid <= 300) return 'needs-improvement'
    return 'poor'
  }

  return [
    { metric: 'CLS', value: metrics.cls, rating: getClsRating(metrics.cls) },
    { metric: 'FCP', value: metrics.fcp, rating: getFcpRating(metrics.fcp) },
    { metric: 'LCP', value: metrics.lcp, rating: getLcpRating(metrics.lcp) },
    { metric: 'FID', value: metrics.fid, rating: getFidRating(metrics.fid) },
    { metric: 'TTFB', value: metrics.ttfb, rating: 'unknown' },
    { metric: 'TTI', value: metrics.tti, rating: 'unknown' }
  ]
}

/**
 * Log Web Vitals summary to console
 */
export function logWebVitals(): void {
  const summary = getWebVitalsSummary()

  console.group('[Web Vitals Summary]')
  summary.forEach(({ metric, value, rating }) => {
    const icon = rating === 'good' ? '✓' : rating === 'needs-improvement' ? '⚠' : rating === 'poor' ? '✗' : '?'
    const valueStr = value !== undefined ? `${value.toFixed(2)}ms` : 'N/A'
    console.log(`${icon} ${metric}: ${valueStr} (${rating})`)
  })
  console.groupEnd()
}
